/* eslint-disable */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Tooltip,
  IconButton
} from '@mui/material';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import KwVolumeCategoryPickerPane from 'components/atoms/modal/KwVolumeCategoryPickerPane';
import { useKwVolumeCategoryPicker } from 'feactures/categoryKeywordVolume/useKwVolumeCategoryPicker';
import {
  currentKwVolumeAmazonCategoryList,
  fetchKwVolumeDashboardSummary,
  fetchCategoryKeywordVolumeReports,
  registerCategoryKeywordVolumeCategory,
  deleteCategoryKeywordVolumeCategory,
  resetKwVolAmazonPicker,
  kwVolumeDashboardSummarySelector,
  kwVolumeDashboardSummaryStatusSelector,
  categoryKeywordVolumeLastReportsSelector,
  categoryKeywordVolumeReportsStatusSelector,
  kwVolScMasterChildrenStatusSelector
} from 'feactures/categoryKeywordVolume/categoryKeywordVolumeSlice';
import {
  formatKwVolumePathLabels,
  kwVolumeHasMoreTiersToPick
} from 'feactures/categoryKeywordVolume/kwVolumePickerHelpers';
import {
  KW_VOLUME_REPORT_TOP_N,
  KW_VOLUME_REPORT_INITIAL_VISIBLE,
  KW_VOLUME_REPORT_WEEK_OPTIONS
} from 'config/kwVolumeReportConfig';

function formatDateJa(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ja-JP');
}

function formatPath(path, options) {
  return formatKwVolumePathLabels(path, options);
}

/** pickerTree 未取得時も CategoryForm が category2〜6 を参照するため全キーを空配列で用意 */
const EMPTY_AMAZON_CATEGORY_TREE = {
  category1: [],
  category2: [],
  category3: [],
  category4: [],
  category5: [],
  category6: []
};

function normalizeReportDate(iso) {
  if (!iso) return '';
  return String(iso).slice(0, 10);
}

/** 当日を含む週の日曜（バックエンド report_date と同じ）を YYYY-MM-DD で返す */
function currentReportWeekEndIso(fromDate = new Date()) {
  const d = new Date(fromDate);
  d.setHours(0, 0, 0, 0);
  const mondayOffset = d.getDay() === 0 ? 6 : d.getDay() - 1;
  d.setDate(d.getDate() - mondayOffset + 6);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const KW_VOLUME_SECTION_MIN_HEIGHT = 280;
const KW_VOLUME_SECTION_SUBTITLE = `登録カテゴリの検索数上位${KW_VOLUME_REPORT_TOP_N}位のキーワードを週ごとに表示します。`;
const KW_FETCH_STATUS_IDLE = 0;
const KW_FETCH_STATUS_PENDING = 1;
const KW_FETCH_STATUS_DONE = 2;

function findMatchedKwVolumeCategory(categories, browseNodeId) {
  if (!Number.isFinite(browseNodeId)) return null;
  return (
    categories.find((c) => Number(c.browse_node_id) === browseNodeId) ||
    categories.find((c) => Number(c.category_id) === browseNodeId) ||
    categories.find((c) => Number(c.id) === browseNodeId) ||
    null
  );
}

function getLeafBrowseNodeId(path) {
  const arr = Array.isArray(path) ? path : [];
  for (let i = arr.length - 1; i >= 0; i--) {
    const slot = arr[i];
    if (!slot || slot === 0) continue;
    if (typeof slot !== 'object') continue;
    const raw = slot.value ?? slot.category_id ?? slot.id;
    if (raw == null || raw === '' || raw === 0) continue;
    const id = Number(raw);
    if (Number.isFinite(id)) return id;
  }
  return null;
}

export default function CategorySearchNeedsSection({ accountId }) {
  const dispatch = useDispatch();
  const selectedPath = useSelector(currentKwVolumeAmazonCategoryList);
  const premium = useSelector((s) => Boolean(s.user?.plan?.amazon?.premium));

  const summary = useSelector(kwVolumeDashboardSummarySelector);
  const summaryStatus = useSelector(kwVolumeDashboardSummaryStatusSelector);
  const reports = useSelector(categoryKeywordVolumeLastReportsSelector);
  const reportsStatus = useSelector(categoryKeywordVolumeReportsStatusSelector);

  const [pickerModalOpen, setPickerModalOpen] = useState(false);
  const [selectedVolumeId, setSelectedVolumeId] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [tableExpanded, setTableExpanded] = useState(false);
  const [registerBusy, setRegisterBusy] = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [deleteConfirmRow, setDeleteConfirmRow] = useState(null);
  const [toast, setToast] = useState('');
  const childrenLoading =
    useSelector(kwVolScMasterChildrenStatusSelector) === 'loading';

  const {
    pickerTree,
    comboGrayscaleIds,
    pickerTrackedCategories,
    pickerLoading,
    registerTargetIsGrayscale
  } = useKwVolumeCategoryPicker({
    open: pickerModalOpen,
    accountId,
    enabled: Boolean(accountId)
  });

  const refreshSummary = useCallback(() => {
    if (!accountId) return;
    dispatch(
      fetchKwVolumeDashboardSummary({
        account_id: accountId,
        premium
      })
    );
  }, [accountId, dispatch, premium]);

  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  const categories = summary?.categories || [];
  const registrationLimit =
    Number(summary?.registration_limit) || (premium ? 10 : 1);
  const registeredCount = categories.length;
  const canAddMore = registeredCount < registrationLimit;

  const leafBrowseNodeId = useMemo(
    () => getLeafBrowseNodeId(selectedPath),
    [selectedPath]
  );

  useEffect(() => {
    if (!pickerModalOpen || !accountId) return;
    refreshSummary();
  }, [pickerModalOpen, accountId, refreshSummary]);

  const matchedCategory = useMemo(() => {
    if (leafBrowseNodeId == null) return null;
    const fromSummary = findMatchedKwVolumeCategory(
      categories,
      leafBrowseNodeId
    );
    if (fromSummary) return fromSummary;
    return findMatchedKwVolumeCategory(
      pickerTrackedCategories,
      leafBrowseNodeId
    );
  }, [leafBrowseNodeId, categories, pickerTrackedCategories]);

  const leafFetchStatus = matchedCategory
    ? Number(matchedCategory.fetch_status)
    : KW_FETCH_STATUS_IDLE;
  const leafStatusLoading =
    leafBrowseNodeId != null &&
    !matchedCategory &&
    summaryStatus === 'loading' &&
    pickerTrackedCategories.length === 0;

  const hasMoreTiersToPick = useMemo(
    () =>
      kwVolumeHasMoreTiersToPick({
        categories: pickerTree || EMPTY_AMAZON_CATEGORY_TREE,
        path: selectedPath,
        childrenLoading
      }),
    [pickerTree, selectedPath, childrenLoading]
  );

  const selectedRow = useMemo(
    () => categories.find((c) => Number(c.id) === Number(selectedVolumeId)),
    [categories, selectedVolumeId]
  );

  useEffect(() => {
    if (!accountId || !selectedVolumeId || !selectedRow?.has_data) return;
    dispatch(
      fetchCategoryKeywordVolumeReports({
        account_id: accountId,
        category_id: selectedVolumeId
      })
    );
  }, [accountId, selectedVolumeId, selectedRow?.has_data, dispatch]);

  useEffect(() => {
    setSelectedWeek('');
  }, [selectedVolumeId]);

  useEffect(() => {
    if (!categories.length) {
      setSelectedVolumeId(null);
      return;
    }
    const stillValid =
      selectedVolumeId != null &&
      categories.some((c) => Number(c.id) === Number(selectedVolumeId));
    if (stillValid) return;
    const preferred =
      categories.find((c) => c.has_data) || categories[0] || null;
    setSelectedVolumeId(preferred ? preferred.id : null);
  }, [categories, selectedVolumeId]);

  const availableWeeks = useMemo(() => {
    const maxWeek = currentReportWeekEndIso();
    const dates = new Set();
    (Array.isArray(reports) ? reports : []).forEach((row) => {
      const d = normalizeReportDate(row.date);
      if (d && d <= maxWeek) dates.add(d);
    });
    return Array.from(dates)
      .sort((a, b) => b.localeCompare(a))
      .slice(0, KW_VOLUME_REPORT_WEEK_OPTIONS);
  }, [reports]);

  useEffect(() => {
    if (!availableWeeks.length) {
      setSelectedWeek('');
      return;
    }
    if (!selectedWeek || !availableWeeks.includes(selectedWeek)) {
      setSelectedWeek(availableWeeks[0]);
    }
  }, [availableWeeks, selectedWeek]);

  const tableRows = useMemo(() => {
    if (!selectedWeek) return [];
    const maxWeek = currentReportWeekEndIso();
    if (selectedWeek > maxWeek) return [];
    const rows = (Array.isArray(reports) ? reports : []).filter(
      (row) => normalizeReportDate(row.date) === selectedWeek
    );
    rows.sort(
      (a, b) => (Number(b.search_volume) || 0) - (Number(a.search_volume) || 0)
    );
    return rows.slice(0, KW_VOLUME_REPORT_TOP_N);
  }, [reports, selectedWeek]);

  useEffect(() => {
    setTableExpanded(false);
  }, [selectedWeek, selectedVolumeId]);

  const visibleTableRows = useMemo(() => {
    if (tableExpanded) return tableRows;
    return tableRows.slice(0, KW_VOLUME_REPORT_INITIAL_VISIBLE);
  }, [tableRows, tableExpanded]);

  const canExpandTable =
    tableRows.length > KW_VOLUME_REPORT_INITIAL_VISIBLE;

  const handleSelectCategory = useCallback((row) => {
    if (!row) return;
    setSelectedVolumeId(row.id);
  }, []);

  const handleOpenPicker = useCallback(() => {
    dispatch(resetKwVolAmazonPicker());
    setPickerModalOpen(true);
  }, [dispatch]);

  const addButtonTooltip = !canAddMore
    ? `${registrationLimit}件まで登録できます`
    : '';

  const handleRegisterCategory = async () => {
    if (!accountId || leafBrowseNodeId == null) return;
    if (registerTargetIsGrayscale || registerBusy) return;
    if (!canAddMore && !matchedCategory) {
      setToast(`${registrationLimit}件まで登録できます。`);
      return;
    }
    const displayName = formatPath(selectedPath);
    const browseId = leafBrowseNodeId;

    setRegisterBusy(true);
    try {
      await dispatch(
        registerCategoryKeywordVolumeCategory({
          account_id: accountId,
          category_id: browseId,
          category_name: displayName || '',
          premium
        })
      ).unwrap();
      setSelectedVolumeId(browseId);
      setPickerModalOpen(false);
      const summaryResult = await dispatch(
        fetchKwVolumeDashboardSummary({
          account_id: accountId,
          premium
        })
      ).unwrap();
      const addedRow = (summaryResult?.categories || []).find(
        (c) => Number(c.id) === Number(browseId)
      );
      setToast(
        addedRow?.has_data
          ? '追加しました。'
          : '追加しました。準備に1〜2日かかります。'
      );
    } catch (e) {
      setToast('追加に失敗しました。しばらくしてから再度お試しください。');
    } finally {
      setRegisterBusy(false);
    }
  };

  const handleDeleteCategory = async (row) => {
    if (!accountId || !premium || !row?.id) return;
    setDeleteBusyId(row.id);
    try {
      await dispatch(
        deleteCategoryKeywordVolumeCategory({
          account_id: accountId,
          category_id: row.id,
          premium
        })
      ).unwrap();
      if (Number(selectedVolumeId) === Number(row.id)) {
        const next =
          categories.find(
            (c) => Number(c.id) !== Number(row.id) && c.has_data
          ) ||
          categories.find((c) => Number(c.id) !== Number(row.id)) ||
          null;
        setSelectedVolumeId(next ? next.id : null);
      }
      setDeleteConfirmRow(null);
      setToast('削除しました。');
      await dispatch(
        fetchKwVolumeDashboardSummary({
          account_id: accountId,
          premium
        })
      );
    } catch (e) {
      setToast('削除に失敗しました。しばらくしてから再度お試しください。');
    } finally {
      setDeleteBusyId(null);
    }
  };

  const canRequestDataFetch = useMemo(() => {
    if (leafBrowseNodeId == null || registerTargetIsGrayscale) return false;
    if (matchedCategory?.has_data) return false;
    if (
      matchedCategory &&
      !matchedCategory.has_data &&
      (leafFetchStatus === KW_FETCH_STATUS_PENDING ||
        leafFetchStatus === KW_FETCH_STATUS_DONE)
    ) {
      return false;
    }
    if (!matchedCategory && !canAddMore) return false;
    return true;
  }, [
    leafBrowseNodeId,
    registerTargetIsGrayscale,
    matchedCategory,
    leafFetchStatus,
    canAddMore
  ]);

  if (!accountId) {
    return (
      <Box width="100%" bgcolor="#FFFFFF" borderRadius={3} sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          アカウント情報を読み込めません。
        </Typography>
      </Box>
    );
  }

  return (
    <Box width="100%" bgcolor="#FFFFFF" borderRadius={3}>
      <Box sx={{ p: 2, minHeight: KW_VOLUME_SECTION_MIN_HEIGHT }}>
        {summaryStatus === 'loading' && (
          <>
            <Typography sx={{ fontSize: 15, fontWeight: 'bold' }}>
              検索上位キーワード
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25, mb: 1.5 }}
            >
              {KW_VOLUME_SECTION_SUBTITLE}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} py={1}>
              <CircularProgress size={24} />
              <Typography variant="body2">読み込み中…</Typography>
            </Box>
          </>
        )}

        {summaryStatus === 'failed' && (
          <>
            <Typography sx={{ fontSize: 15, fontWeight: 'bold' }}>
              検索上位キーワード
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25, mb: 1.5 }}
            >
              {KW_VOLUME_SECTION_SUBTITLE}
            </Typography>
            <Typography variant="body2" color="error" paragraph>
              読み込めませんでした。しばらくしてから再度お試しください。
            </Typography>
          </>
        )}

        {summary?.master_unavailable && summaryStatus === 'succeeded' && (
          <>
            <Typography sx={{ fontSize: 15, fontWeight: 'bold' }}>
              検索上位キーワード
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25, mb: 1.5 }}
            >
              {KW_VOLUME_SECTION_SUBTITLE}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              一時的に利用できません。しばらくしてから再度お試しください。
            </Typography>
          </>
        )}

        {summaryStatus !== 'loading' &&
          summaryStatus !== 'failed' &&
          !summary?.master_unavailable && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1.5
                }}
              >
                <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 'bold' }}>
                    検索上位キーワード
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.25 }}
                  >
                    {KW_VOLUME_SECTION_SUBTITLE}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Typography
                    variant="caption"
                    color={!canAddMore ? 'warning.main' : 'text.secondary'}
                    sx={{ fontWeight: !canAddMore ? 600 : 400 }}
                  >
                    登録 {registeredCount}/{registrationLimit}
                  </Typography>
                  {!premium && categories.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      変更は有料プランで可能
                    </Typography>
                  )}
                  <Tooltip title={addButtonTooltip} disableHoverListener={canAddMore}>
                    <span>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!canAddMore}
                        onClick={handleOpenPicker}
                      >
                        追加
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>

              {categories.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  カテゴリがありません。「追加」から選んでください。
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                    width: '100%'
                  }}
                >
                  <FormControl
                    size="small"
                    sx={{ flex: '1 1 240px', minWidth: 0 }}
                  >
                    <InputLabel id="kw-sn-category">表示カテゴリ</InputLabel>
                    <Select
                      labelId="kw-sn-category"
                      label="表示カテゴリ"
                      value={
                        selectedVolumeId != null ? String(selectedVolumeId) : ''
                      }
                      title={selectedRow?.category_name || ''}
                      renderValue={(value) => {
                        const row = categories.find(
                          (c) => String(c.id) === String(value)
                        );
                        if (!row) return '';
                        const name = row.category_name || '（名称なし）';
                        if (!row.has_data) {
                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                minWidth: 0,
                                width: '100%'
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  minWidth: 0,
                                  flex: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {name}
                              </Box>
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ flexShrink: 0 }}
                              >
                                準備中
                              </Typography>
                            </Box>
                          );
                        }
                        return (
                          <Box
                            component="span"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block'
                            }}
                          >
                            {name}
                          </Box>
                        );
                      }}
                      onChange={(e) => {
                        const id = Number(e.target.value);
                        const row = categories.find(
                          (c) => Number(c.id) === id
                        );
                        if (row) handleSelectCategory(row);
                      }}
                      sx={{
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': {
                          py: 0.75,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }
                      }}
                    >
                      {categories.map((row) => {
                        const name = row.category_name || '（名称なし）';
                        return (
                          <MenuItem
                            key={row.id}
                            value={String(row.id)}
                            title={name}
                            sx={{
                              fontSize: '0.8125rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 3
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                minWidth: 0,
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {name}
                            </Box>
                            {!row.has_data && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ flexShrink: 0 }}
                              >
                                準備中
                              </Typography>
                            )}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>

                  {premium && selectedRow && (
                    <Tooltip title="このカテゴリを削除">
                      <span>
                        <IconButton
                          size="small"
                          color="inherit"
                          disabled={deleteBusyId === selectedRow.id}
                          onClick={() => setDeleteConfirmRow(selectedRow)}
                          sx={{ color: 'text.secondary', flexShrink: 0 }}
                          aria-label="このカテゴリを削除"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {selectedRow?.has_data && (
                    <FormControl
                      size="small"
                      sx={{
                        width: 128,
                        flexShrink: 0,
                        '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                        '& .MuiSelect-select': {
                          fontSize: '0.8125rem',
                          py: 0.75
                        }
                      }}
                    >
                      <InputLabel id="kw-sn-week">週</InputLabel>
                      <Select
                        labelId="kw-sn-week"
                        label="週"
                        value={selectedWeek || ''}
                        onChange={(e) => setSelectedWeek(e.target.value)}
                        disabled={!availableWeeks.length}
                      >
                        {availableWeeks.map((week) => (
                          <MenuItem
                            key={week}
                            value={week}
                            sx={{ fontSize: '0.8125rem' }}
                          >
                            {formatDateJa(week)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}

              {selectedRow && !selectedRow.has_data && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25
                  }}
                >
                  <ScheduleOutlinedIcon
                    fontSize="small"
                    sx={{ mt: 0.25, color: 'text.secondary', flexShrink: 0 }}
                  />
                  <Box minWidth={0}>
                    <Typography variant="body2" fontWeight={600}>
                      準備中（1〜2日）
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      準備が終わるとキーワードが表示されます。
                    </Typography>
                  </Box>
                </Box>
              )}

              {selectedRow?.has_data && (
                <Box mb={2}>
                  {reportsStatus === 'loading' && (
                    <Box display="flex" alignItems="center" gap={1} py={2}>
                      <CircularProgress size={24} />
                      <Typography variant="body2">読み込み中…</Typography>
                    </Box>
                  )}

                  {reportsStatus === 'failed' && (
                    <Typography variant="body2" color="error" py={2}>
                      読み込めませんでした。しばらくしてから再度お試しください。
                    </Typography>
                  )}

                  {reportsStatus !== 'loading' && reportsStatus !== 'failed' && (
                    <>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                align="right"
                                sx={{ width: 56, whiteSpace: 'nowrap' }}
                              >
                                順位
                              </TableCell>
                              <TableCell>キーワード</TableCell>
                              <TableCell align="right">検索数</TableCell>
                              <TableCell align="right">前年比</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tableRows.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    データがありません。
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              visibleTableRows.map((row, idx) => (
                                <TableRow
                                  key={`${selectedWeek}-${row.keyword}-${idx}`}
                                >
                                  <TableCell align="right">
                                    {idx + 1}
                                  </TableCell>
                                  <TableCell>{row.keyword}</TableCell>
                                  <TableCell align="right">
                                    {row.search_volume != null
                                      ? Number(
                                          row.search_volume
                                        ).toLocaleString('ja-JP')
                                      : '—'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.yoy != null
                                      ? `${Number(row.yoy).toFixed(1)}%`
                                      : '—'}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {canExpandTable && (
                        <Box
                          display="flex"
                          justifyContent="center"
                          mt={1}
                        >
                          <Button
                            size="small"
                            onClick={() =>
                              setTableExpanded((prev) => !prev)
                            }
                          >
                            {tableExpanded
                              ? '折りたたむ'
                              : `すべて表示（${tableRows.length}件）`}
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}
            </>
          )}
      </Box>

      <Dialog
        open={Boolean(deleteConfirmRow)}
        onClose={() => {
          if (deleteBusyId == null) setDeleteConfirmRow(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem' }}>削除しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            「
            {deleteConfirmRow?.category_name || 'このカテゴリ'}
            」を削除します。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button
            size="small"
            disabled={deleteBusyId != null}
            onClick={() => setDeleteConfirmRow(null)}
          >
            キャンセル
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            disabled={deleteBusyId != null}
            onClick={() => {
              if (deleteConfirmRow) handleDeleteCategory(deleteConfirmRow);
            }}
          >
            {deleteBusyId != null ? '削除中…' : '削除'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pickerModalOpen}
        onClose={() => setPickerModalOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: { maxWidth: 440, width: '100%' }
        }}
      >
        <DialogTitle sx={{ py: 1.25, fontSize: '1rem' }}>
          カテゴリを追加
        </DialogTitle>
        <DialogContent dividers sx={{ py: 1.5 }}>
          <KwVolumeCategoryPickerPane
            inline
            categories={pickerTree || EMPTY_AMAZON_CATEGORY_TREE}
            comboGrayscaleIds={comboGrayscaleIds}
            pickerInitialLoading={pickerLoading}
          />

          <Box
            sx={{
              mt: 1.5,
              p: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography
              variant="body2"
              sx={{ flex: '1 1 220px', minWidth: 0 }}
              title={formatPath(selectedPath) || undefined}
              noWrap
            >
              {formatPath(selectedPath) || 'カテゴリを選択してください'}
            </Typography>

            {leafBrowseNodeId != null && registerTargetIsGrayscale && (
              <Typography variant="caption" color="text.secondary">
                このカテゴリは対象外です。
              </Typography>
            )}

            {leafBrowseNodeId != null &&
              !registerTargetIsGrayscale &&
              leafStatusLoading && (
                <Typography variant="body2" color="text.secondary">
                  確認中…
                </Typography>
              )}

            {leafBrowseNodeId != null &&
              !registerTargetIsGrayscale &&
              !leafStatusLoading &&
              matchedCategory?.has_data && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    handleSelectCategory(matchedCategory);
                    setPickerModalOpen(false);
                  }}
                >
                  表示する
                </Button>
              )}

            {leafBrowseNodeId != null &&
              !registerTargetIsGrayscale &&
              !leafStatusLoading &&
              matchedCategory &&
              !matchedCategory.has_data &&
              (leafFetchStatus === KW_FETCH_STATUS_PENDING ||
                leafFetchStatus === KW_FETCH_STATUS_DONE) && (
                <Typography variant="body2" color="text.disabled">
                  準備中（1〜2日）
                </Typography>
              )}

            {leafBrowseNodeId != null &&
              !registerTargetIsGrayscale &&
              hasMoreTiersToPick && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  下の階層も選べます
                </Typography>
              )}

            {leafBrowseNodeId != null &&
              !registerTargetIsGrayscale &&
              canRequestDataFetch && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={registerBusy}
                  onClick={handleRegisterCategory}
                >
                  追加する
                </Button>
              )}

            {!canAddMore && !matchedCategory && (
              <Typography variant="caption" color="error">
                {registrationLimit}件まで登録できます。
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: 1 }}>
          <Button onClick={() => setPickerModalOpen(false)} size="small">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
